import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DynamicListComponent } from "../dynamic-list/dynamic-list.component";
import { CommissionsReportTileComponent } from "../commissions-report-tile/commissions-report-tile.component";
import { Pagination } from "../../services/pagination.service";
import { CommissionReportItem } from "../../models/reports.model";
import { BehaviorSubject } from "rxjs";
import { shareReplay, take, tap } from "rxjs/operators";
import { ReportsService } from "../../services/reports.service";
import { BusinessService } from "../../services/business.service";
import { SupabaseService } from "../../services/supabase.service";
import { ViewAsService } from "../../services/view-as.service";
import { dateString } from "../../../../../common/src/lib/services";
import { BusinessProfile } from "../../../../../common/src/lib/models/business-profile.model";
import { limitStep } from "../../services/reports-service-extras";
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

function commissionItemsToPaginationList(items: CommissionReportItem[], kind: 'Pending' | 'Paid') {
  return items.map(item => ({
    componentClass: CommissionsReportTileComponent,
    header: dateString(item.createdAt),
    args: { item, kind }
  }));
}

@Component({
  selector: 'app-commissions-report-items',
  templateUrl: './commissions-report-items.component.html',
  styleUrls: ['./commissions-report-items.component.scss']
})
export class CommissionsReportItemsComponent implements OnInit, OnDestroy {

  @Input() current!: 'Pending' | 'Paid';

  @ViewChild('pendingList') pendingList!: DynamicListComponent<CommissionsReportTileComponent>;
  @ViewChild('pendingDateFilterList') pendingDateFilterList!: DynamicListComponent<CommissionsReportTileComponent>;
  @ViewChild('paidList') paidList!: DynamicListComponent<CommissionsReportTileComponent>;
  @ViewChild('paidDateFilterList') paidDateFilterList!: DynamicListComponent<CommissionsReportTileComponent>;

  pendingPagination!: Pagination<CommissionReportItem>;
  pendingDateFilterPagination!: Pagination<CommissionReportItem>;
  paidPagination!: Pagination<CommissionReportItem>;
  paidDateFilterPagination!: Pagination<CommissionReportItem>;

  loading$ = new BehaviorSubject<boolean>(true);

  dateFilter$ = this.reportsService.selectedDateRangeForEvent$.pipe(
    tap(async dates => {
      if (dates) {
        this.loadItems('Pending', 0, undefined, dates, true);
        this.loadItems('Paid', 0, undefined, dates, true);
      } else {
        this.pendingDateFilterList?.clear();
        this.paidDateFilterList?.clear();
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true})
  );

  constructor(
    private reportsService: ReportsService,
    private businessService: BusinessService,
    private supabaseService: SupabaseService,
    private viewAsService: ViewAsService,
  ) { }

  async ngOnInit() {
    const business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;

    this.pendingPagination = this.createPagination(business.businessId, 'commissions_pending_report', 'c_pen')
      .on(
        '*',
        'commission',
          _ => this.handleChange('Pending')
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Pending')
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('Pending')
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.pendingList)
      );
    this.pendingDateFilterPagination = this.createPagination(business.businessId, 'commissions_pending_report', 'c_pen_d')
      .on(
        '*',
        'commission',
          _ => this.handleChange('Pending', true)
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Pending', true)
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('Pending', true)
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.pendingDateFilterList)
      );
    this.paidPagination = this.createPagination(business.businessId, 'commissions_paid_report', 'c_paid')
      .on(
        '*',
        'commission',
          _ => this.handleChange('Paid')
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Paid')
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('Paid')
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.paidList)
      );
    this.paidDateFilterPagination = this.createPagination(business.businessId, 'commissions_paid_report', 'c_paid_d')
      .on(
        '*',
        'commission',
          _ => this.handleChange('Paid', true)
      )
      .on(
        'UPDATE',
        'invoice_version_client',
        _ => this.handleChange('Paid', true)
      )
      .on(
        'UPDATE',
        'estimate_version_client',
        _ => this.handleChange('Paid', true)
      )
      .on(
        'UPDATE',
        'workflow',
        payload => this.handleWorkflowChange(payload, this.paidDateFilterList)
      );

    await this.loadItems('Pending', 0, business, undefined, false);
    await this.loadItems('Paid', 0, business, undefined, false);
    this.loading$.next(false);
  }

  ngOnDestroy() {
    this.pendingPagination?.destroy();
    this.pendingDateFilterPagination?.destroy();
    this.paidPagination?.destroy();
    this.paidDateFilterPagination?.destroy();
  }

  async loadItems(
    type: 'Pending' | 'Paid',
    inOffset = 0,
    business?: BusinessProfile,
    dates?: [Date, Date] | null,
    dateList?: boolean,
    updateList?: boolean
  ) {
    if (!business)
      business = (await this.businessService.selectedBusiness$.pipe(take(1)).toPromise())!;

    if (dateList && !dates)
      dates = await this.dateFilter$.pipe(take(1)).toPromise();
    if (!dates)
      dates = [business.createdAt, new Date()];

    const selectedUsers = await this.viewAsService.selectedUsersIds$.pipe(take(1)).toPromise();

    const pagination = type === 'Pending'
      ? !dateList
        ? this.pendingPagination
        : this.pendingDateFilterPagination
      : !dateList
        ? this.paidPagination
        : this.paidDateFilterPagination;

    const list = type === 'Pending'
      ? !dateList
        ? this.pendingList
        : this.pendingDateFilterList
      : !dateList
        ? this.paidList
        : this.paidDateFilterList;

    const inLimit = updateList && limitStep < list.getBottomCount ? list.getBottomCount : limitStep;

    const items = await pagination.get({
      startTime: dates[0],
      endTime: dates[1],
      rowLimit: inLimit,
      offsetCount: inOffset,
      selectedUsers: selectedUsers ?? []
    });

    if (items.length < inLimit && list.canLoadMoreBottom)
      list.canLoadMoreBottom = false;

    if (!inOffset)
      list.clear();
    list?.addAtBottom(commissionItemsToPaginationList(items, type));
  }

  async handleChange(type: string, dateList = false) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    const dates = await this.dateFilter$.pipe(take(1)).toPromise();

    this.loadItems(
      type as 'Pending' | 'Paid',
      0,
      business!,
      dateList ? dates : null,
      dateList,
      true
    );
  }

  handleWorkflowChange(payload: RealtimePostgresChangesPayload<any>, list: DynamicListComponent<CommissionsReportTileComponent>) {
    const data = payload.new;
    list.updateItems(component => {
      const report = component.instance.item;
      if (report.wId === data.id)
        report.jobType = data.jobType;
      return false;
    });
  }

  createPagination(businessId: string, fn: string, cname: string) {
    return new Pagination<CommissionReportItem>(
      this.supabaseService,
      fn,
      businessId,
      cname
    );
  }
}
